import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Stack, TextField, Tooltip, Typography } from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import { DeviationPreview, NoteDetail, ReweightingLine, ReweightingReferences, toLineInput, WeightLineInput } from '../../../../Models/ReWeightingModels/Workspace';
import { downloadReweightingAttachment, downloadReweightingPdf, needsReload, previewReweightingNote, reweightingError, saveReweightingNote, submitReweightingNote, uploadReweightingAttachment } from '../../../../Services/ReweightingWorkspaceService';
import WeightLineDialog from './WeightLineDialog';
import NoteContext from './NoteContext';
import NoteStatus from './NoteStatus';
import WeightLinesTable from './WeightLinesTable';
import { formatDate, formatKg } from '../../workspaceFormatting';
import SourceNotesSection from './SourceNotesSection';
import AttachmentField from '../../../../SharedComponents/Documents/AttachmentField';
import DocumentActions from '../../../../SharedComponents/Documents/DocumentActions';

interface Props {
  detail: NoteDetail; references?: ReweightingReferences; compact: boolean; refreshing: boolean;
  onDirty: (dirty: boolean) => void; onBusy: (busy: boolean) => void;
  onSaved: (detail: NoteDetail) => void; onSubmitted: (message: string) => void;
  onReload: () => void; onBack: () => void;
}
export default function NoteEditor({ detail, references, compact, refreshing, onDirty, onBusy, onSaved, onSubmitted, onReload, onBack }: Props) {
  const [editing, setEditing] = useState<ReweightingLine | 'new' | null>(null);
  const [removing, setRemoving] = useState<ReweightingLine | null>(null);
  const [busy, setBusy] = useState<'save' | 'preview' | 'submit' | 'upload' | null>(null);
  const [error, setError] = useState('');
  const [mustReload, setMustReload] = useState(false);
  const [preview, setPreview] = useState<DeviationPreview | null>(null);
  const [reason, setReason] = useState('');
  const [highlightedId, setHighlightedId] = useState<number | null>(null);
  const saving = useRef(false);
  const { note } = detail;
  const lines = detail.weights;
  const editable = note.canEdit && !busy && !mustReload && !refreshing;
  const attachmentReady = !detail.documents?.requiresAttachment ||
    (detail.documents.attachment?.noteId === note.id && !detail.documents.attachment.submittedAt);
  useEffect(() => { onDirty(editing !== null || preview !== null); return () => onDirty(false); }, [editing, preview, onDirty]);
  useEffect(() => { onBusy(!!busy); return () => onBusy(false); }, [busy, onBusy]);
  useEffect(() => {
    if (highlightedId === null) return;
    const timeout = window.setTimeout(() => setHighlightedId(null), 2500);
    return () => window.clearTimeout(timeout);
  }, [highlightedId]);

  const failure = (value: unknown) => {
    setError(reweightingError(value));
    setMustReload(needsReload(value));
  };
  const persist = async (inputs: WeightLineInput[]) => {
    if (saving.current || !editable) throw new Error('Bíddu þar til vinnslu er lokið áður en þú heldur áfram.');
    saving.current = true;
    setBusy('save'); setError('');
    try {
      const saved = await saveReweightingNote(detail, inputs);
      onSaved(saved);
      return saved;
    }
    catch (value) { failure(value); throw value; }
    finally { saving.current = false; setBusy(null); }
  };
  const saveLine = async (value: WeightLineInput) => {
    const inputs = lines.map(toLineInput);
    const saved = await persist(value.id === null ? [...inputs, value] : inputs.map(line => line.id === value.id ? value : line));
    setHighlightedId(value.id ?? saved.weights.find(line => !lines.some(previous => previous.id === line.id))?.id ?? null);
    setEditing(null);
  };
  const removeLine = async () => {
    if (!removing || lines.length < 2) return;
    try {
      await persist(lines.filter(line => line.id !== removing.id).map(toLineInput));
      setRemoving(null);
    } catch {
      // Villan birtist í staðfestingarglugganum; ekkert er fjarlægt úr töflunni fyrr en vistun tekst.
    }
  };
  const review = async () => {
    setBusy('preview'); setError(''); setReason('');
    try { setPreview(await previewReweightingNote(detail)); }
    catch (value) { failure(value); }
    finally { setBusy(null); }
  };
  const upload = async (file: File) => {
    if (saving.current || !editable) return;
    saving.current = true; setBusy('upload'); setError('');
    try {
      const saved = await uploadReweightingAttachment(detail, file);
      onSaved(saved);
      // Aðeins fylgiskjalið breyttist; frávikaforskoðun gildir áfram með nýrri útgáfu.
      setPreview(current => current ? { ...current, version: saved.version } : null);
    } catch (value) { failure(value); throw value; }
    finally { saving.current = false; setBusy(null); }
  };
  const submit = async () => {
    if (!preview || !editable || !attachmentReady) return;
    setBusy('submit'); setError('');
    try {
      const result = await submitReweightingNote(note.id, preview.version, reason);
      setPreview(null);
      onSubmitted(result.requiresReview ? 'Endurvigtun móttekin. Frávik bíður yfirferðar.' : 'Endurvigtun móttekin og nótunni lokað.');
    } catch (value) { failure(value); }
    finally { setBusy(null); }
  };
  const canUpload = note.canEdit && !(detail.documents?.attachment?.noteId === note.id && detail.documents.attachment.submittedAt);
  const attachmentSection = detail.documents && (detail.documents.attachment || canUpload) && <Box sx={{ my: 2.5 }}>
    <AttachmentField attachment={detail.documents.attachment} load={() => downloadReweightingAttachment(note.id)}
      onUpload={canUpload ? upload : undefined} disabled={!!busy || mustReload || refreshing} uploading={busy === 'upload'} />
    {detail.documents.attachment && detail.documents.attachment.noteId !== note.id && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
      Fylgiskjalið var sent með fyrri nótu #{detail.documents.attachment.noteId}.</Typography>}
  </Box>;
  return (
    <Box component="section" aria-label={`Vigtarnóta ${note.id}`} sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column',
      '@keyframes note-appear': { from: { opacity: 0 }, to: { opacity: 1 } }, animation: 'note-appear 160ms ease-out',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', xl: 'minmax(0, 1fr) 264px' },
        gridTemplateAreas: { xs: '"summary" "context" "weights"', xl: '"summary context" "weights context"' },
        gridTemplateRows: { xs: 'auto auto 1fr', xl: 'auto 1fr' } }}>
        <Box sx={{ gridArea: 'summary', minWidth: 0, px: { xs: 2, sm: 3 }, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }} gap={1}>
            {compact ? <Button startIcon={<ArrowBackRounded />} onClick={onBack} disabled={!!busy}>Nótulisti</Button>
              : <Typography variant="body2" color="text.secondary">Nóta <Box component="span" fontWeight={600}>#{note.id}</Box></Typography>}
            <Stack direction="row" spacing={2} alignItems="center">
              <NoteStatus status={note.status} />
              <Tooltip title="Sækja nýjustu útgáfu"><span><IconButton aria-label="Sækja nýjustu útgáfu" onClick={onReload} disabled={!!busy || refreshing} size="small" sx={{ border: 1, borderColor: 'divider' }}><RefreshRounded fontSize="small" /></IconButton></span></Tooltip>
            </Stack>
          </Stack>
          <Typography component="h1" sx={{ fontSize: { xs: 23, sm: 28 }, letterSpacing: '-0.7px', lineHeight: 1.3, overflowWrap: 'anywhere' }}>
            <Box component="span" fontWeight={800} sx={{ fontSize: { xs: 27, sm: 32 }, mr: 0.5 }}>{note.shipNumber}</Box>{' '}{note.shipName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>Löndun {formatDate(note.landedAt)} · {note.harbourName} · {note.gearName}</Typography>
        </Box>
        <NoteContext detail={detail} />
        <Box sx={{ gridArea: 'weights', minWidth: 0, p: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 } }}>
          <SourceNotesSection detail={detail} disabled={!!busy || mustReload || refreshing} />
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1.5} sx={{ mb: 2 }}>
            <Box><Typography component="h2" fontWeight={700} fontSize={18}>Frá vigtunarleyfishafa</Typography>
              <Typography variant="caption" color="text.secondary">Nóta #{note.id} · Nettóafli án íss</Typography></Box>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
              {detail.documents && <Tooltip title={detail.documents.canDownloadFinal ? '' : detail.documents.finalPdfUnavailableReason || 'PDF verður aðgengilegt eftir staðfest skil.'}>
                <Box tabIndex={detail.documents.canDownloadFinal ? undefined : 0} aria-label={detail.documents.canDownloadFinal ? undefined : detail.documents.finalPdfUnavailableReason || 'PDF verður aðgengilegt eftir staðfest skil.'}>
                  <DocumentActions fileName={`vigtarnota_${note.id}.pdf`} previewLabel="Vigtarnóta (PDF)" load={() => downloadReweightingPdf(note.id)}
                    disabled={!!busy || mustReload || refreshing || !detail.documents.canDownloadFinal} />
                </Box>
              </Tooltip>}
              {note.canEdit && <Button size="small" variant="outlined" startIcon={<AddRounded />} disabled={!editable || !references}
                onClick={() => { setError(''); setEditing('new'); }}>Ný lína</Button>}
            </Stack>
          </Stack>
          {note.comment && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{note.comment}</Typography>}
          {!note.canEdit && (note.hasPendingDeviation || !note.isClosed) && <Alert severity={note.hasPendingDeviation ? 'warning' : 'info'} sx={{ mb: 2.5 }}>
            {note.hasPendingDeviation ? 'Skil eru móttekin. Frávik bíður yfirferðar hjá höfn eða Fiskistofu.' : note.readOnlyReason}
          </Alert>}
          {note.firstDueDate && note.dueDate && note.firstDueDate !== note.dueDate && <Alert severity="info" sx={{ my: 2 }}>Vigtarlínur hafa mismunandi skilafresti. Síðasti skráði frestur er sýndur í yfirliti; vistaður frestur hverrar línu birtist við aflann.</Alert>}
          {error && !preview && <Alert severity="error" sx={{ my: 2 }} action={mustReload ? <Button color="inherit" onClick={onReload}>Sækja aftur</Button> : undefined}>{error}</Alert>}
          {!note.canEdit && attachmentSection}
          <WeightLinesTable lines={lines} editable={editable && !!references} highlightedId={highlightedId}
            showDeadlines={note.firstDueDate !== note.dueDate}
            onEdit={note.canEdit ? line => { setError(''); setEditing(line); } : undefined}
            onRemove={note.canEdit ? line => { setError(''); setRemoving(line); } : undefined} />
          {note.canEdit && attachmentSection}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: { xs: 'block', xl: 'none' } }}>
          Nóta #{note.id} · Löndun #{note.landingId}{detail.originalNoteId && ` · Grunnnóta #${detail.originalNoteId}`}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ height: 2, flexShrink: 0 }}>{(busy || refreshing) && <LinearProgress sx={{ height: 2 }} aria-label="Vinnsla í gangi" />}</Box>
      {note.canEdit && <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between"
        spacing={1.5} sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Stack direction="row" spacing={1} alignItems="center" role="status" aria-live="polite" sx={{ color: mustReload ? 'warning.main' : error ? 'error.main' : 'text.secondary' }}>
          {busy ? <CircularProgress size={15} color="inherit" /> : error ? <ErrorOutlineRounded sx={{ fontSize: 18 }} /> : <CheckCircleOutlineRounded sx={{ fontSize: 18 }} />}
          <Typography variant="body2">
            {busy === 'save' ? 'Vista breytingu…' : busy === 'upload' ? 'Vista fylgiskjal…' : busy === 'preview' ? 'Yfirferð í gangi…' : busy === 'submit' ? 'Skila endurvigtun…' :
              mustReload ? 'Sækja þarf nótuna aftur' : error ? 'Aðgerð tókst ekki' : 'Vistað · bíður skila'}
          </Typography>
        </Stack>
        <Button variant="contained" endIcon={<ArrowForwardRounded />} disabled={!editable || lines.length === 0} onClick={review}>
          Yfirfara og skila
        </Button>
      </Stack>}
      {editing && references && <WeightLineDialog detail={detail} references={references}
        line={editing === 'new' ? undefined : editing} saving={busy === 'save'} reloadRequired={mustReload}
        onClose={() => setEditing(null)} onSave={saveLine} />}
      <Dialog open={!!removing} onClose={busy ? undefined : () => setRemoving(null)} maxWidth="xs" fullWidth aria-labelledby="remove-line-title">
        <DialogTitle id="remove-line-title">Fjarlægja vigtarlínu?</DialogTitle>
        <DialogContent>
          <Typography fontWeight={600}>{removing?.fishStockName} · {formatKg(removing?.quantity ?? 0)} kg</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{removing?.conditionName} · {removing?.storageMethodName}</Typography>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button disabled={!!busy} onClick={() => setRemoving(null)}>Hætta við</Button>
          <Button color="error" variant="contained" disabled={!editable} onClick={removeLine}
            startIcon={busy === 'save' ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {busy === 'save' ? 'Fjarlægi…' : 'Fjarlægja línu'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!preview} onClose={busy ? undefined : () => setPreview(null)} maxWidth="sm" fullWidth aria-labelledby="submit-note-title">
        <DialogTitle id="submit-note-title">Skila endurvigtun</DialogTitle>
        <DialogContent dividers>
          <Typography fontWeight={600}>{note.shipNumber} {note.shipName} · Nóta #{note.id}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>Nótunni verður lokað við skil. Breytingar eftir skil fara í gegnum löndunarhöfn.</Typography>
          {detail.documents && <Box sx={{ mb: 3 }}>
            <AttachmentField attachment={detail.documents.attachment} load={() => downloadReweightingAttachment(note.id)}
              onUpload={upload} disabled={!!busy || mustReload || refreshing} uploading={busy === 'upload'} />
            {!attachmentReady && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Hladdu upp fylgiskjali á þessa nótu til að geta staðfest skil.</Typography>}
          </Box>}
          <Stack direction="row" justifyContent="space-between" sx={{ py: 2 }}><Typography>Nettóafli til skila</Typography><Typography fontWeight={700}>{formatKg(preview?.quantity ?? 0)} kg</Typography></Stack>
          <Alert severity={preview?.hasDeviation ? 'warning' : 'success'} icon={preview?.hasDeviation ? undefined : <CheckCircleOutlineRounded />}>
            {preview?.hasDeviation ? 'Frávik fannst frá upprunalegri skráningu. Skil fara í yfirferð.' : 'Engin frávik fundust við yfirferð.'}
          </Alert>
          {preview?.hasDeviation && <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Línur til skila</Typography>
            {detail.weights.map(line => <Box key={line.id} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction="row" justifyContent="space-between" gap={2}>
                <Typography variant="body2" fontWeight={600}>{line.fishStockName} · {line.conditionName}{line.isUnderKilogram && ' · undirmál'}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{formatKg(line.quantity)} kg</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">{line.storageMethodName} · {line.destinyName} · {line.fishingAreaName}</Typography>
              {preview.deviationLineIds.includes(line.id) && <Typography variant="caption" display="block" color="warning.main">Frávik við yfirferð</Typography>}
            </Box>)}
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Upprunalegar línur</Typography>
            {detail.originalWeights.map(line => <Box key={line.id} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction="row" justifyContent="space-between" gap={2}>
                <Typography variant="body2">{line.fishStockName} · {line.conditionName}{line.isUnderKilogram && ' · undirmál'}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{formatKg(line.quantity)} kg</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">{line.storageMethodName} · {line.destinyName} · {line.fishingAreaName}</Typography>
            </Box>)}
          </Box>}
          {preview?.hasDeviation && <TextField autoFocus required fullWidth multiline minRows={3} sx={{ mt: 3 }} label="Skýring á fráviki"
            value={reason} onChange={event => setReason(event.target.value)} inputProps={{ maxLength: 4000 }} disabled={!!busy}
            helperText="Útskýrðu hvað breyttist og hvers vegna." />}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}{mustReload && ' Lokaðu glugganum og sæktu nótuna aftur áður en þú heldur áfram.'}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button disabled={!!busy} onClick={() => setPreview(null)}>Til baka</Button>
          <Button variant="contained" disableElevation disabled={!!busy || mustReload || !attachmentReady || (!!preview?.hasDeviation && !reason.trim())}
            startIcon={busy === 'submit' ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineRounded />} onClick={submit}>Staðfesta skil</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
